import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { liquidityForecast, parseAmountToCents, type LiquidityBucket, type LiquidityForecast } from "@talentkontor/domain";
import { useStaffWorkspace, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { useInvoices } from "../../../data/queries";
import { ErrorState, Loading, NoAccess } from "../../../components/States";
import { KopfMehr, PageHead } from "../../../components/ui";
import { BetragErkannt, betragUnlesbar } from "../../../components/Erkannt";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import { date, money, today as todayIn } from "../../../lib/format";
import type { PayoutInfo } from "./detail-data";
import { erstattungName, liquidityItems, offeneErstattung, payoutInvoiceIds } from "./liquiditaet-model";
import type { InvoiceRow } from "../../../data/queries";
import "./rechnungen.css";
import { t } from "../../../lib/sprache";
import { useNurLesen } from "../../../app/nurLesen";

/**
 * Liquiditätsvorschau (0070): offene Forderungen gegen Marken und Creator nach Fälligkeit neben fälligen Auszahlungen an
 * Creator, je Kalenderwoche und je Währung (EUR und CHF getrennt). Überfälliges und Posten ohne Termin stehen gesondert.
 * Nur mit dem Recht „Beträge sehen“. Weg: /agentur/kennzahlen/liquiditaet?waehrung=CHF&wochen=12
 * 0099 (Tarifstaffel): Die Vorschau gehört zu „Kontoauszug-Import und Liquiditätsvorschau“; ohne das Modul bleibt die Seite
 * erreichbar und zeigt den Tarif statt der Vorschau (die Daten selbst sperrt nur die Oberfläche).
 */

const WOCHEN = [8, 12, 16];

export function Liquiditaet() {
  const ws = useStaffWorkspace();
  if (!ws.can("view_amounts")) return <NoAccess text={t("Die Liquiditätsvorschau zeigt Beträge. Ein Admin kann das Recht „Beträge sehen“ unter Einstellungen → Team vergeben.", "The liquidity forecast shows amounts. An admin can grant the “View amounts” permission under Settings → Team.")} />;
  return <LiquiditaetSeite key={ws.org.org_id} ws={ws} />;
}

function LiquiditaetSeite({ ws }: { ws: StaffWorkspace }) {
  const modul = useModul("module_bank_sync");
  if (modul.gesperrt) return <LiquiditaetGesperrt ws={ws} />;
  return <LiquiditaetVorschau ws={ws} />;
}

/** Ohne Modul: Seite mit Kopf und Wegen, statt der Vorschau der Hinweis auf den Tarif. */
function LiquiditaetGesperrt({ ws }: { ws: StaffWorkspace }) {
  // K-1/K-2: Pause und Nur-Lesen
  const nurLesen = useNurLesen();
  const canReconcile = ws.can("confirm_payments") && (ws.isChef || ws.membership.sees_all_talents);
  return (
    <main id="main" className="content">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Kennzahlen", "Metrics")}</div>
          <h1>{t("Liquiditätsvorschau", "Liquidity forecast")}</h1>
          <p>{t("Offene Forderungen gegen fällige Auszahlungen an Creator, je Kalenderwoche und Währung.", "Open receivables against due payouts to creators, per calendar week and currency.")}</p>
        </div>
      </div>
      <div className="row">
        <Link className="btn" to="/agentur/rechnungen">{t("Rechnungen", "Invoices")}</Link>
        {canReconcile && !nurLesen && <Link className="btn" to="/agentur/rechnungen/kontoauszug">{t("Kontoauszug abgleichen", "Reconcile bank statement")}</Link>}
        {ws.can("view_financials") && <Link className="btn" to="/agentur/kennzahlen">{t("Kennzahlen", "Metrics")}</Link>}
      </div>
      <TarifHinweis modul="module_bank_sync" was={t("Liquiditätsvorschau", "Liquidity forecast")} />
      <p className="muted">{t("Offene Rechnungen und fällige Auszahlungen siehst du weiter in der Rechnungsliste.", "You still see open invoices and due payouts in the invoice list.")}</p>
    </main>
  );
}

function LiquiditaetVorschau({ ws }: { ws: StaffWorkspace }) {
  // K-1/K-2: Pause und Nur-Lesen
  const nurLesen = useNurLesen();
  const orgId = ws.org.org_id;
  const today = todayIn(ws.org.timezone);
  const invoices = useInvoices(orgId);
  const ids = payoutInvoiceIds(invoices.data ?? []);
  const payout = useQuery({ queryKey: ["invoice_payout_info", orgId, ids.join(",")], enabled: ids.length > 0,
    queryFn: () => api.read<PayoutInfo[]>("invoice_payout_info", { p_invoices: ids }).catch(() => [] as PayoutInfo[]) });
  // Offene Auszahlungsläufe: Ausführungsdatum je Rechnung. Wer Auszahlungen nicht sehen darf, bekommt eine leere Liste und
  // die Vorschau rechnet wie bisher mit dem Zahlungsziel.
  const laeufe = useQuery({ queryKey: ["payout_run_dates", orgId], queryFn: async () => {
    const [runs, items] = await Promise.all([
      api.select<{ id: string; status: string; execution_date: string }>("payout_runs", { columns: "id, status, execution_date", filters: [["org_id", "eq", orgId], ["status", "in", ["open", "exported"]]] }).catch(() => []),
      api.select<{ run_id: string; invoice_id: string; status: string }>("payout_run_items", { columns: "run_id, invoice_id, status", filters: [["org_id", "eq", orgId], ["status", "eq", "open"]] }).catch(() => []),
    ]);
    const datum = new Map(runs.map((r) => [r.id, r.execution_date]));
    return new Map(items.filter((i) => datum.has(i.run_id)).map((i) => [i.invoice_id, datum.get(i.run_id)!]));
  } });
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [weeks, setWeeks] = useState(() => (WOCHEN.includes(Number(params.get("wochen"))) ? Number(params.get("wochen")) : 12));
  const [cur, setCur] = useState<string | null>(() => params.get("waehrung"));
  const [opening, setOpening] = useState<Record<string, string>>({});

  const openingCents = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [c, v] of Object.entries(opening)) {
      try {
        if (v.trim()) out[c] = parseAmountToCents(v);
      } catch {
        // ungültige Eingabe: ohne Kontostand rechnen; die Meldung steht unter dem Feld (Gegenprüfung GB-4)
      }
    }
    return out;
  }, [opening]);
  const forecasts = useMemo(() => (invoices.data && (ids.length === 0 || payout.data)
    ? liquidityForecast(liquidityItems(invoices.data, payout.data ?? [], today, laeufe.data ?? null), { today, weeks, openingCents }) : null),
  // ids ist aus invoices abgeleitet
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [invoices.data, payout.data, laeufe.data, today, weeks, openingCents]);

  if (invoices.error) return <ErrorState error={invoices.error} retry={() => void invoices.refetch()} />;
  if (!forecasts) return <Loading />;
  const currencies = forecasts.map((f) => f.currency);
  const f = forecasts.find((x) => x.currency === cur) ?? forecasts.find((x) => x.currency === ws.org.currency) ?? forecasts[0] ?? null;
  const byId = new Map((invoices.data ?? []).map((i) => [i.id, i]));
  const restricted = !ws.isChef && !ws.membership.sees_all_talents;
  const canReconcile = ws.can("confirm_payments") && (ws.isChef || ws.membership.sees_all_talents);

  return (
    <main id="main" className="content tk-liquiditaet">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Kennzahlen", "Metrics")}</div>
          <h1>{t("Liquiditätsvorschau", "Liquidity forecast")}</h1>
          <p>{t("Offene Forderungen gegen fällige Auszahlungen an Creator, je Kalenderwoche und Währung.", "Open receivables against due payouts to creators, per calendar week and currency.")}</p>
        </div>
        <div className="overview-filter">
          {currencies.length > 1 && (
            <label><span className="tk-filter-name">{t("Währung", "Currency")}</span><select aria-label={t("Währung", "Currency")} value={f?.currency ?? ""} onChange={(e) => setCur(e.target.value)}>
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          )}
          <label><span className="tk-filter-name">{t("Zeitraum", "Period")}</span><select aria-label={t("Zeitraum", "Period")} value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
              {WOCHEN.map((w) => <option key={w} value={w}>{w} {t("Wochen", "Weeks")}</option>)}
            </select>
          </label>
          {f && (
            <label><span className="tk-filter-name">{t("Kontostand heute (", "Account balance today (")}{f.currency})</span>
              <input inputMode="decimal" placeholder={t("Kontostand (optional)", "Balance (optional)")} value={opening[f.currency] ?? ""} aria-invalid={betragUnlesbar(opening[f.currency] ?? "", true) || undefined} onChange={(e) => setOpening({ ...opening, [f.currency]: e.target.value })} />
              <BetragErkannt text={opening[f.currency] ?? ""} currency={f.currency} negative />
            </label>
          )}
          {/* Kompakt (D-1): Wege zu Rechnungen, Kontoauszug und Kennzahlen im Seitenkopf hinter „Mehr“ statt in einer eigenen Zeile */}
          <KopfMehr>
            <Link to="/agentur/rechnungen">{t("Rechnungen", "Invoices")}</Link>
            {canReconcile && !nurLesen && <Link to="/agentur/rechnungen/kontoauszug">{t("Kontoauszug abgleichen", "Reconcile bank statement")}</Link>}
            {ws.can("view_financials") && <Link to="/agentur/kennzahlen">{t("Kennzahlen", "Metrics")}</Link>}
          </KopfMehr>
        </div>
      </div>
      {restricted && <div className="notice">{t("Du siehst die Rechnungen deiner zugewiesenen Creator. Für die ganze Agentur braucht es die Sicht auf alle Creator.", "You see the invoices of your assigned creators. For the whole agency you need the view of all creators.")}</div>}

      {!f ? (
        <div className="panel stack tk-empty">
          <h3>{t("Keine offenen Posten", "No open items")}</h3>
          <p>{t("Es gibt keine offenen Forderungen und keine fälligen Auszahlungen. Versendete Rechnungen und freigegebene Creator-Rechnungen erscheinen hier.", "There are no open receivables and no due payouts. Sent invoices and approved creator invoices appear here.")}</p>
        </div>
      ) : (
        <Vorschau f={f} weeks={weeks} opening={openingCents[f.currency] ?? null} byId={byId} />
      )}
      <p className="source-note">
        {t("Forderungen: endgültig erstellte, offene Rechnungen der Agentur an Marken und Creator nach Fälligkeit (Teilzahlungen abgezogen, ohne Stornorechnungen und ausgebuchte Forderungen). Auszahlungen: freigegebene, noch nicht ausgezahlte Rechnungen und Gutschriften der Creator nach der Auszahlungsregel der Agentur; mit „Auszahlung nach Zahlung der Marke“ erst ab der Zahlung. Erstattungen: Ist eine stornierte Rechnung schon bezahlt und noch nicht erstattet, steht der Betrag als Abfluss mit Fälligkeit heute; eine stornierte, schon ausgezahlte Rechnung eines Creators als Rückforderung bei den Eingängen. Der kumulierte Saldo beginnt mit dem Überfälligen", "Receivables: issued, open invoices of the agency to brands and creators by due date (partial payments deducted, without cancellation invoices and written-off receivables). Payouts: approved, not yet paid out invoices and self-billing invoices of the creators according to the agency's payout rule; with “Payout after the brand's payment” only from the payment. Refunds: if a cancelled invoice has already been paid and not yet refunded, the amount appears as an outflow due today; a cancelled creator invoice that was already paid out appears as a reclaim under receipts. The cumulative balance starts with the overdue amount")}{Object.keys(openingCents).length ? t(" und dem eingegebenen Kontostand", " and the entered account balance") : ""}{t(". Währungen werden nie zusammengerechnet.", ". Currencies are never added together.")}</p>
    </main>
  );
}

function Vorschau({ f, weeks, opening, byId }: { f: LiquidityForecast; weeks: number; opening: number | null; byId: Map<string, Pick<InvoiceRow, "number" | "status" | "kind" | "currency" | "paid_cents" | "gross_cents" | "paid_at" | "credit_for">> }) {
  const m = (c: number) => money(c, f.currency);
  const signed = (c: number) => (c > 0 ? `+${m(c)}` : c < 0 ? `−${m(-c)}` : m(0));
  const last = f.weeks[f.weeks.length - 1];
  const max = Math.max(1, ...f.weeks.flatMap((w) => [w.inCents, w.outCents]));
  // Praxistest K-153: Erstattungen und Rückforderungen nach einem Storno heißen auch so (nicht „Rechnung“ unter Auszahlungen)
  const erstattung = (id: string) => { const row = byId.get(id); return row ? offeneErstattung(row) : null; };
  const items = (b: LiquidityBucket) => {
    if (b.items.length === 0) return false;
    const storno = b.items.filter((id) => erstattung(id)?.kind === "out").length;
    const zurueck = b.items.filter((id) => erstattung(id)?.kind === "in").length;
    const rechnungen = b.items.length - storno - zurueck;
    const teile = [
      rechnungen > 0 ? (rechnungen === 1 ? t("1 Rechnung", "1 invoice") : t(`${rechnungen} Rechnungen`, `${rechnungen} invoices`)) : null,
      storno > 0 ? (storno === 1 ? t("1 Erstattung nach Storno", "1 refund after cancellation") : t(`${storno} Erstattungen nach Storno`, `${storno} refunds after cancellation`)) : null,
      zurueck > 0 ? (zurueck === 1 ? t("1 Rückforderung nach Storno", "1 reclaim after cancellation") : t(`${zurueck} Rückforderungen nach Storno`, `${zurueck} reclaims after cancellation`)) : null,
    ].filter(Boolean);
    return (
      <details className="tk-liqitems">
        <summary>{teile.join(" · ")}</summary>
        <span>{b.items.map((id, k) => {
          const e = erstattung(id);
          return <span key={id}>{k > 0 ? ", " : ""}<Link to={`/agentur/rechnungen/${id}`}>{e ? `${erstattungName(e.kind)} ` : ""}{byId.get(id)?.number ?? t("ohne Nummer", "without number")}</Link></span>;
        })}</span>
      </details>
    );
  };
  const erstattungenAus = [...byId.values()].filter((r) => r.currency === f.currency).map((r) => offeneErstattung(r)).filter((e) => e?.kind === "out").reduce((a, e) => a + (e?.cents ?? 0), 0);
  const row = (key: string, label: string, sub: string | null, b: LiquidityBucket, cumulative: number | null, tone?: string) => (
    <tr key={key}>
      <td data-label={t("Zeitraum", "Period")}><span className="tk-invcell"><span className="tk-liq-zeitraum"><strong>{label}</strong>{sub && <small>{sub}</small>}</span>{items(b)}</span></td>
      <td data-label={t("Eingänge", "Receipts")} className="money"><span className="num">{b.inCents ? m(b.inCents) : "–"}</span></td>
      <td data-label={t("Auszahlungen", "Payouts")} className="money"><span className="num">{b.outCents ? `−${m(b.outCents)}` : "–"}</span></td>
      <td data-label={t("Saldo", "Balance")} className="money"><span className={`num${tone ? ` ${tone}` : ""}`}>{signed(b.inCents - b.outCents)}</span></td>
      <td data-label={t("Kumuliert", "Cumulative")} className="money">{cumulative === null ? <span className="muted">–</span> : <span className={`num ${cumulative < 0 ? "tk-neg" : ""}`}>{signed(cumulative)}</span>}</td>
    </tr>
  );
  return (
    <>
      <section>
        <div className="grid four kpis">
          <div className="kpi">
            <small>{t("Offene Forderungen · ", "Open receivables · ")}{f.currency}</small>
            <div className="metric num">{m(f.totalInCents)}</div>
            <small>{t("davon überfällig ", "of which overdue ")}{m(f.overdue.inCents)}{f.notSent.inCents ? t(` · ohne Termin (nicht versendet oder nicht zugegangen) ${m(f.notSent.inCents)}`, ` · without date (not sent or not received) ${m(f.notSent.inCents)}`) : ""}</small>
          </div>
          <div className="kpi">
            <small>{erstattungenAus > 0 ? t("Auszahlungen und Erstattungen", "Payouts and refunds") : t("Auszahlungen an Creator", "Payouts to creators")}</small>
            <div className="metric num">{m(f.totalOutCents)}</div>
            <small>{t("davon überfällig ", "of which overdue ")}{m(f.overdue.outCents)}{f.waitingBrand.outCents ? t(` · nach Zahlung der Marke ${m(f.waitingBrand.outCents)}`, ` · after the brand's payment ${m(f.waitingBrand.outCents)}`) : ""}{erstattungenAus > 0 ? t(` · Erstattungen nach Storno ${m(erstattungenAus)}`, ` · refunds after cancellation ${m(erstattungenAus)}`) : ""}</small>
          </div>
          <div className="kpi">
            <small>{t("In den nächsten ", "In the next ")}{weeks} {t("Wochen", "Weeks")}</small>
            <div className="metric num">{signed(f.weeks.reduce((s, w) => s + w.netCents, 0))}</div>
            <small>{m(f.weeks.reduce((s, w) => s + w.inCents, 0))} {t("Eingänge · ", "receipts · ")}{m(f.weeks.reduce((s, w) => s + w.outCents, 0))} {erstattungenAus > 0 ? t("Auszahlungen und Erstattungen", "payouts and refunds") : t("Auszahlungen", "Payouts")}</small>
          </div>
          <div className="kpi">
            <small>{t("Saldo Ende KW ", "Balance end of week ")}{last?.week}</small>
            <div className={`metric num ${last && last.cumulativeCents < 0 ? "tk-neg" : ""}`}>{last ? signed(last.cumulativeCents) : "–"}</div>
            <small>{opening !== null ? t(`mit Kontostand ${m(opening)} und Überfälligem`, `with account balance ${m(opening)} and overdue amount`) : t("ab heute, mit Überfälligem, ohne Kontostand", "from today, with overdue amount, without account balance")}</small>
          </div>
        </div>
      </section>

      <section className="panel stack" aria-labelledby="liq-chart">
        <h3 id="liq-chart">{t("Je Woche", "Per week")}</h3>
        <div className="chart-legend"><span><i style={{ background: "var(--success)" }} />{t("Eingänge", "Receipts")}</span><span><i style={{ background: "var(--error)" }} />{t("Auszahlungen", "Payouts")}</span></div>
        <div className="tk-liqplot" role="img" aria-label={t(`Eingänge und Auszahlungen je Woche in ${f.currency}: ${f.weeks.map((w) => t(`KW ${w.week} ${m(w.inCents)} ein, ${m(w.outCents)} aus`, `Wk ${w.week} ${m(w.inCents)} in, ${m(w.outCents)} out`)).join("; ")}`, `Receipts and payouts per week in ${f.currency}: ${f.weeks.map((w) => t(`KW ${w.week} ${m(w.inCents)} ein, ${m(w.outCents)} aus`, `Wk ${w.week} ${m(w.inCents)} in, ${m(w.outCents)} out`)).join("; ")}`)}>
          <div className="chart tk-liqchart">
            {f.weeks.map((w) => (
              <div key={w.start} className="tk-liqweek">
                <div className="bar tk-bar-success" style={{ height: `${Math.max(2, (w.inCents / max) * 100)}%` }} title={t(`KW ${w.week}: Eingänge ${m(w.inCents)}`, `Wk ${w.week}: receipts ${m(w.inCents)}`)} />
                <div className="bar tk-bar-error" style={{ height: `${Math.max(2, (w.outCents / max) * 100)}%` }} title={t(`KW ${w.week}: Auszahlungen ${m(w.outCents)}`, `Wk ${w.week}: payouts ${m(w.outCents)}`)} />
              </div>
            ))}
          </div>
          <div className="chartlabels tk-liqlabels">{f.weeks.map((w) => <span key={w.start}><span className="tk-kw">{t("KW ", "Wk ")}</span>{w.week}</span>)}</div>
        </div>
      </section>

      <div className="tablewrap tk-cardtable tk-liqtable tk-sticky-end">
        <table>
          <caption className="sr-only">{t("Liquiditätsvorschau je Woche in ", "Liquidity forecast per week in ")}{f.currency}</caption>
          <thead>
            <tr><th scope="col">{t("Zeitraum", "Period")}</th><th scope="col" className="money">{t("Eingänge", "Receipts")}</th><th scope="col" className="money">{t("Auszahlungen", "Payouts")}</th><th scope="col" className="money">{t("Saldo", "Balance")}</th><th scope="col" className="money">{t("Kumuliert", "Cumulative")}</th></tr>
          </thead>
          <tbody>
            {row("overdue", t("Überfällig", "Overdue"), t("fällig vor heute", "due before today"), f.overdue, (opening ?? 0) + f.overdue.inCents - f.overdue.outCents, "tk-warn")}
            {f.weeks.map((w) => row(w.start, `KW ${w.week}`, t(`${date(w.start)} bis ${date(w.end)}`, `${date(w.start)} to ${date(w.end)}`), w, w.cumulativeCents))}
            {last && row("later", t("Später", "Later"), `ab ${date(addWeek(last.end))}`, f.later, null)}
            {(f.notSent.items.length > 0 || f.waitingBrand.items.length > 0) && row("undated", t("Ohne Termin", "Without date"), t("Rechnungen noch nicht versendet oder nicht zugegangen bzw. Auszahlungen erst nach Zahlung der Marke", "Invoices not yet sent or not received, or payouts only after the brand's payment"),
              { inCents: f.notSent.inCents, outCents: f.waitingBrand.outCents, items: [...f.notSent.items, ...f.waitingBrand.items] }, null)}
          </tbody>
        </table>
      </div>
    </>
  );
}

function addWeek(end: string): string {
  const d = new Date(`${end}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
